import {
  getReviewsByAPI, voteReviewByAPI,
  type ReviewResponseDTO,
  type ICreateReviewDTO,
  createReview,
  getRating,
} from '~/utils/services/api/reviews/reviews';
import {QUERY_KEYS} from '~/utils/services/keys';
import {SortReviewsOptions, type ICpArticle} from '~/types';
import type {NuxtError} from 'nuxt/app';
import {getAFCatalogArticles} from '~/utils/services/api/ms-catalog/ms-catalog';

interface GetReviewsOptions {
  articleId: string;
  pageNumber: number;
  search?: string;
  pageSize?: number;
  sortType: SortReviewsOptions;
}

interface GetMyReviewsOptions {
  pageNumber: number;
  search?: string;
  pageSize?: number;
  sortType: SortReviewsOptions;
}

export interface ReviewViewModel {
  id: string;
  articleId: string;
  rating: number;
  userPic: string;
  title: string;
  body: string;
  userName: string;
  date: string;
  verifiedPurchase: boolean;
  negativeVotes: number;
  positiveVotes: number;
}


export interface ReviewWithArticleViewModel extends ReviewViewModel {
  article: {
    title: string;
    isOffer: boolean;
    picture: string;
    tPrice: string;
    sku: string;
    price: string;
    link: string;
  };
}


export interface ReviewsViewModel {
  meta: {
    totalItems: number;
    totalPages: number;
  },
  data: ReviewViewModel[];
}

export interface ReviewsWithArticleViewModel extends ReviewsViewModel {
  data: ReviewWithArticleViewModel[];
}

export interface ReviewIdViewModel {
  data: { id: string } | undefined;
}

export interface UserReviewByArticleOptions {
  productId: string;
}

export interface VoteReviewOptions {
  productId: string;
  reviewId: string;
  vote: number;
}

export interface ReviewWithArticlesResponseDTO extends ReviewResponseDTO {
  articles: ICpArticle[];
}

const convertSortReviewsOptions = (sortType: SortReviewsOptions) => {
  switch (sortType) {
    case SortReviewsOptions.SORT_BY_POSITIVE_VOTES:
      return '-positive_votes';
    case SortReviewsOptions.SORT_BY_MOST_RECENT:
      return '-created_at';
    case SortReviewsOptions.SORT_BY_OLDEST_REVIEW:
      return 'created_at';
    case SortReviewsOptions.SORT_BY_BEST_RATING:
      return '-rating';
    case SortReviewsOptions.SORT_BY_WORST_RATING:
      return 'rating';
  }
};

const generateKey = (prefix: string, params: any) => {
  return prefix + '__' + Object.keys(params).map((key) => params[key]).join('-');
};

export const useCreateReview = (review: ICreateReviewDTO) => {
  return useAsyncData(QUERY_KEYS.reviews.create, () => createReview(review));
};

export const useGetArticleReviews = (params: GetReviewsOptions) => {
  return useAsyncData<ReviewResponseDTO, NuxtError, ReviewsViewModel>(
    generateKey('reviews-by-article', params),
    () => getReviewsByAPI({
      'sort': convertSortReviewsOptions(params.sortType),
      'page[number]': params.pageNumber,
      'page[size]': params.pageSize,
      'filter[article_id]': params.articleId,
      'filter[status]': 'active',
      'filter[search]': params.search || undefined,
    }),
    {
      transform: (input: ReviewResponseDTO): ReviewsViewModel => {
        return {
          meta: {
            totalItems: input.meta.total,
            totalPages: input.meta.last_page,
          },
          data: input.data.map((r) => {
            return {
              id: r.id,
              articleId: r.attributes.article_id,
              rating: r.attributes.rating,
              userPic: r.attributes.user_pic,
              title: r.attributes.title,
              body: r.attributes.body,
              userName: r.attributes.user_name,
              date: new Date(r.attributes.created_at).toLocaleDateString(),
              verifiedPurchase: !!r.attributes.verified_purchase,
              negativeVotes: r.attributes.negative_votes,
              positiveVotes: r.attributes.positive_votes,
            };
          }),
        };
      },
    },
  );
};

export const useReviewVote = async (options: VoteReviewOptions) => {
  const {userId} = useCpUser();
  return useAsyncData(
    QUERY_KEYS.reviews.vote,
    () => voteReviewByAPI({
      vote: options.vote,
      userId: userId ?? '',
      reviewId: options.reviewId,
    }),
  );
};
export const useGetArticleReviewByUser = (params: UserReviewByArticleOptions) => {
  const {userId} = useCpUser();
  return useAsyncData<ReviewResponseDTO, NuxtError, ReviewIdViewModel>(
    generateKey('review-by-article-by-user', params),
    () => {
      if (!userId) {
        return rejectQueryFn();
      }

      return getReviewsByAPI({
        'page[number]': 1,
        'page[size]': 1,
        'filter[article_id]': params.productId,
        'filter[status]': 'active',
        'filter[user_id]': userId ?? '',
      });
    },
    {
      transform: (input: ReviewResponseDTO): ReviewIdViewModel => {
        if (input.data && input.data.length) {
          return {data: {id: input.data[0].id}};
        }

        return {data: undefined};
      },
    },
  );
};


export const useGetMyReviews = (params: GetMyReviewsOptions) => {
  const {userId} = useCpUser();

  // if (!userEmail || !userId) {
  //   return rejectQueryFn();
  // }

  return useAsyncData<ReviewWithArticlesResponseDTO, NuxtError, ReviewsWithArticleViewModel>(
    generateKey('reviews-by-article', params),
    async () => {
      const reviewsResponse = await getReviewsByAPI({
        'sort': convertSortReviewsOptions(params.sortType),
        'page[number]': params.pageNumber,
        'page[size]': params.pageSize,
        'filter[user_id]': userId!,
        'filter[status]': 'active',
        'filter[search]': params.search ?? undefined,
      });

      const articlesIds = reviewsResponse.data.map((a) => a.attributes.article_id);
      const articlesResponse = await getAFCatalogArticles(articlesIds);

      return {
        ...reviewsResponse,
        articles: (articlesResponse as any).data,
      };
    },
    {
      transform: (input: ReviewWithArticlesResponseDTO): ReviewsWithArticleViewModel => {
        return {
          meta: {
            totalItems: input.meta.total,
            totalPages: input.meta.last_page,
          },
          data: input.data.map((r) => {
            const articleFind = input.articles.find((a) => a.id === r.attributes.article_id);
            const articleData = articleFind ? JSON.parse(JSON.stringify(articleFind)) : undefined;

            return {
              id: r.id,
              articleId: r.attributes.article_id,
              rating: r.attributes.rating,
              userPic: r.attributes.user_pic,
              title: r.attributes.title,
              body: r.attributes.body,
              userName: r.attributes.user_name,
              date: new Date(r.attributes.created_at).toLocaleDateString(),
              verifiedPurchase: !!r.attributes.verified_purchase,
              negativeVotes: r.attributes.negative_votes,
              positiveVotes: r.attributes.positive_votes,
              article: {
                title: articleData.title,
                isOffer: articleData.isOffer,
                picture: articleData.picture,
                tPrice: articleData.tPrice,
                sku: articleData.sku,
                price: articleData.price,
                link: articleData.link,
              },
            };
          }),
        };
      },
    },
  );
};

export const useGetRating = (idProduct: string) => {
  return useAsyncData(QUERY_KEYS.reviews.rating(idProduct), () => {
    return getRating(idProduct);
  });
};
